import { StorefrontCart } from '@jbhi-fi/apis/src/storefront/types';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { formatMoney } from '../FormatMoney';
import { JbCart, JbCartDiscount, JbCartItem, JbCartItemDiscount } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { OnlineProduct } from '@jbhi-fi/apis/src/shopify/types/OnlineProduct';
import { BasketPromoProps } from '@jbhi-fi/utils/src/toBasketPromos';
import { ServiceIds } from '../constants/services';

const GIFT_CARD_HANDLE = 'gift-card-digital-500';

const convertPriceToJb = (price: string): number => {
  return Math.round(parseFloat(price) * 100);
};

export const transformStorefrontCartToJbCart = (storefrontCart: StorefrontCart | undefined): JbCart | undefined => {
  if (!storefrontCart) return;

  const cartAttributes = storefrontCart.attributes.reduce(
    (acc: Record<string, string>, attr: { key: string; value: string }) => {
      acc[attr.key] = attr.value;
      return acc;
    },
    {}
  );

  const cartLevelDiscounts = tryParseJson<Array<JbCartDiscount>>(cartAttributes._cartDiscounts);
  cartLevelDiscounts?.forEach(discount => {
    discount.formattedAmount = formatMoney(discount.amount);
  });

  const lineItemDiscounts = tryParseJson<Array<BasketPromoProps>>(cartAttributes._basketSerial) ?? [];

  const items: Array<JbCartItem> = storefrontCart.lines.edges.map((edge, index) => {
    const node = edge.node;
    const productVariant = node.merchandise;
    const product = productVariant.product;

    const lineItemProperties: JbCartItem['properties'] = {};
    node.attributes.forEach((attr: { key: string; value: string }) => {
      lineItemProperties[attr.key as keyof JbCartItem['properties']] = attr.value;
    });

    const variantId = productVariant.id.split('/').pop() ?? '';
    const productId = product.id.split('/').pop() ?? '';
    const lineId = node.id.split('/').pop() ?? '';
    const totalAmount = convertPriceToJb(node.cost.totalAmount.amount);
    const subtotalAmount = convertPriceToJb(node.cost.subtotalAmount.amount);

    const discounts = lineItemDiscounts?.filter(d => d.promoId === lineId);
    const isNoLongerAvailable = node.quantity === 0;
    const isAttachedService = lineItemProperties._isAttachedService === 'true';
    const hasServiceErrors = isAttachedService && lineItemProperties._hasServiceErrors === 'true';
    const isGiftCard = product.productType === 'Gift Card';
    const handle = isGiftCard ? GIFT_CARD_HANDLE : product.handle;
    const parentLineId = node.parentRelationship?.parent.id.split('/').pop() ?? undefined;

    return {
      id: variantId,
      cartIndex: index + 1,
      variant_id: variantId,
      line_id: lineId,
      key: lineId,
      handle,
      url: `${window.location.origin}/products/${handle}`,
      sku: productVariant.sku,
      image: productVariant.image?.url,
      quantity: node.quantity || 1, // Quantity comes back as 0 if the item is no longer available, which breaks stuff. See: https://github.com/Shopify/storefront-api-feedback/discussions/186
      product_id: productId,
      product_title: product.title,
      product_type: product.productType,
      vendor: product.vendor,
      line_price: totalAmount,
      original_line_price: subtotalAmount,
      final_line_price: totalAmount,
      discounts,
      isNoLongerAvailable,
      isValidService: !hasServiceErrors,
      hide: isAttachedService,
      isGiftCard,
      properties: lineItemProperties,
      onlineProduct: tryParseJson<OnlineProduct>(productVariant.product?.onlineProduct?.value),
      parent_line_id: parentLineId,
    };
  });
  const totalPrice = convertPriceToJb(storefrontCart.cost.totalAmount.amount);
  const subtotalPrice = convertPriceToJb(storefrontCart.cost.subtotalAmount.amount);
  const totalDiscount = storefrontCart.discountAllocations.reduce(
    (sum, allocation) => sum + convertPriceToJb(allocation.discountedAmount.amount),
    0
  );

  // Put attached services on the main item
  items.forEach(item => {
    if (item.properties._isAttachedService === 'true') return;
    const matchingAttachedServices = items.filter(
      attached =>
        attached.properties._isAttachedService === 'true' &&
        attached.properties?._serviceLink === item.properties._serviceLink
    );
    if (!matchingAttachedServices.length) return;

    const attachedService =
      matchingAttachedServices.find(service => service.properties._serviceType !== ServiceIds.Install) ??
      matchingAttachedServices[0];

    if (!attachedService) return;
    item.attachedService = attachedService;
  });

  return {
    items,
    id: storefrontCart.id.split('gid://shopify/Cart/')[1],
    item_count: storefrontCart.totalQuantity,
    total_price: totalPrice,
    original_total_price: subtotalPrice,
    total_discount: totalDiscount,
    cartDiscounts: cartLevelDiscounts,
    attributes: cartAttributes,
    preferredStore: storefrontCart.buyerIdentity?.preferences?.delivery?.pickupHandle?.[0],
  };
};
